/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { FilterItem } from 'src/clu/appShell/CLUTestModelsFiltersBlade';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FilteredItemPills } from 'src/modules/modelGuidance/components/FilteredItemPills';

const messages = defineMessages({
    filtersApplied: {
        id: 'clu.cluModelFilteredLabels.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAllLabels: {
        id: 'clu.cluModelFilteredLabels.clearAllLabels',
        defaultMessage: 'Clear all {filterType} labels '
    },
    clearAllPredictions: {
        id: 'clu.cluModelFilteredLabels.clearAllPredictions',
        defaultMessage: 'Clear all {filterType} predictions'
    },
    clearAllResults: {
        id: 'clu.cluModelFilteredLabels.clearAllResults',
        defaultMessage: 'Clear all results'
    },
    entity: {
        id: 'clu.cluModelFilteredLabels.entity',
        defaultMessage: 'entity'
    },
    intent: {
        id: 'clu.cluModelFilteredLabels.intent',
        defaultMessage: 'intent'
    }
});

type CluModelFilteredLabelsProps = {
    intentLabelsToFilter: FilterItem[];
    onRemoveIntentLabelFilter: (itemName: string) => void;
    onClearIntentLabelFilters: () => void;

    intentPredictionToFilter: FilterItem[];
    onRemoveIntentPredictionFilter: (itemName: string) => void;
    onClearIntentPredictionFilters: () => void;

    entityLabelsToFilter: FilterItem[];
    onRemoveEntityLabelFilter: (itemName: string) => void;
    onClearEntityLabelFilters: () => void;

    entityPredictionToFilter: FilterItem[];
    onRemoveEntityPredictionFilter: (itemName: string) => void;
    onClearEntityPredictionFilters: () => void;

    resultsToFilter: FilterItem[];
    onRemoveResultFilter: (itemName: string) => void;
    onClearResultFilters: () => void;
};

export const CluModelFilteredLabels = (props: CluModelFilteredLabelsProps) => {
    const {
        intentLabelsToFilter,
        onRemoveIntentLabelFilter,
        onClearIntentLabelFilters,
        intentPredictionToFilter,
        onRemoveIntentPredictionFilter,
        onClearIntentPredictionFilters,
        entityLabelsToFilter,
        onRemoveEntityLabelFilter,
        onClearEntityLabelFilters,
        entityPredictionToFilter,
        onRemoveEntityPredictionFilter,
        onClearEntityPredictionFilters,
        resultsToFilter,
        onRemoveResultFilter,
        onClearResultFilters
    } = props;

    const { localize } = useLocalization();

    return (
        <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
            <LuisText aria-label={localize(messages.filtersApplied)}>{localize(messages.filtersApplied)}</LuisText>

            <Stack>
                <FilteredItemPills
                    filteredItemsToShow={intentLabelsToFilter}
                    onRemoveFilter={onRemoveIntentLabelFilter}
                    onClearFilters={onClearIntentLabelFilters}
                    clearFilteredItemsText={localize(messages.clearAllLabels, { filterType: localize(messages.intent) })}
                />
                <FilteredItemPills
                    filteredItemsToShow={intentPredictionToFilter}
                    onRemoveFilter={onRemoveIntentPredictionFilter}
                    onClearFilters={onClearIntentPredictionFilters}
                    clearFilteredItemsText={localize(messages.clearAllPredictions, { filterType: localize(messages.intent) })}
                />
                <FilteredItemPills
                    filteredItemsToShow={entityLabelsToFilter}
                    onRemoveFilter={onRemoveEntityLabelFilter}
                    onClearFilters={onClearEntityLabelFilters}
                    clearFilteredItemsText={localize(messages.clearAllLabels, { filterType: localize(messages.entity) })}
                />
                <FilteredItemPills
                    filteredItemsToShow={entityPredictionToFilter}
                    onRemoveFilter={onRemoveEntityPredictionFilter}
                    onClearFilters={onClearEntityPredictionFilters}
                    clearFilteredItemsText={localize(messages.clearAllPredictions, { filterType: localize(messages.entity) })}
                />
                <FilteredItemPills
                    filteredItemsToShow={resultsToFilter}
                    onRemoveFilter={onRemoveResultFilter}
                    onClearFilters={onClearResultFilters}
                    clearFilteredItemsText={localize(messages.clearAllResults)}
                />
            </Stack>
        </Stack>
    );
};
